import startCase from "lodash.startcase";
import { DateTime } from "luxon";
import React, { ReactNode } from "react";
import { v4 } from "uuid";

import { IgTypography } from "@intelligems/ig-theme";
import {
  OrganizationUsages,
  OrgShopIdDto,
  StripeApiCouponDto,
  StripeApiCreateSubscriptionSettingDto,
  StripeApiCustomerDto,
  StripeApiPriceDto,
  StripeApiPriceTemplateDto,
  StripeApiProductDto,
  StripeApiSubscriptionSchedulePhaseDto,
  SubscriptionBillingProvider,
  SubscriptionCollectionMethod,
  SubscriptionProrationBehavior,
} from "@intelligems/ig-types";

type BaseSubscriptionReducerType = {
  status: string;
  type: "withPhases" | "withoutPhases";
  currentPeriodEnd: number;
  customer?: StripeApiCustomerDto | undefined | null;
  settings: StripeApiCreateSubscriptionSettingDto;
  phases: StripeApiSubscriptionSchedulePhaseDto[];
  phaseIndex: number;
  orgs: OrgShopIdDto[];
  tabIndex: number;
  billingProvider: SubscriptionBillingProvider;
  agencyId: string | null;
  usages: OrganizationUsages | null;
  warnings: Record<string, ReactNode>;
  pendingChanges: boolean;
  cancelAt?: number | null | undefined;
};

export type CreateSubscriptionReducerType = BaseSubscriptionReducerType & {
  mode: "create";
};

export type ModifySubscriptionReducerType = BaseSubscriptionReducerType & {
  mode: "modify";
  stripeSubscriptionId: string;
  subscriptionLinkIds: string[];
};

export type CreateOrModifySubscriptionReducerType =
  | CreateSubscriptionReducerType
  | ModifySubscriptionReducerType;

// eslint-disable-next-line react-refresh/only-export-components
export const initialCreateSubscriptionReducerState: CreateOrModifySubscriptionReducerType =
  {
    status: "active",
    type: "withoutPhases",
    currentPeriodEnd: 0,
    mode: "create",
    agencyId: null,
    orgs: [],
    billingProvider: "stripe",
    phases: [
      {
        phaseStart: 0,
        phaseEnd: 0,
        items: [],
        coupon: null,
        trialEndTs: Math.round(DateTime.now().plus({ days: 7 }).toSeconds()),
      },
    ],
    phaseIndex: 0,
    customer: null,
    settings: {
      autoScale: false,
      collectionMethod: "chargeAutomatically",
      prorationBehavior: "always_invoice",
    },
    tabIndex: 0,
    warnings: {},
    usages: null,
    pendingChanges: false,
  };

export type CreateSubscriptionReducerAction =
  | {
      type: "reset";
    }
  | {
      type: "initModify";
      value: ModifySubscriptionReducerType;
    }
  | { type: "setOrgs"; value: OrgShopIdDto[] }
  | { type: "setAgencyId"; value: string | null }
  | { type: "setUsages"; value: OrganizationUsages | null }
  | { type: "setPhaseIndex"; value: number }
  | { type: "setBillingProvider"; value: SubscriptionBillingProvider }
  | {
      type: "addItem";
      value: StripeApiProductDto;
    }
  | {
      type: "removeItemByProductId";
      value: string;
    }
  | {
      type: "removeItemByPriceId";
      value: string;
    }
  | {
      type: "setItemsByTemplate";
      template: StripeApiPriceTemplateDto;
      quantity: number;
      prices: StripeApiPriceDto[];
      priceIds: string[];
      productId: string;
    }
  | {
      type: "addPriceToItemByIdx";
      value: StripeApiPriceDto;
      priceId?: string;
    }
  | {
      type: "removeItemByIdx";
      idx: string;
    }
  | {
      type: "addPriceToProduct";
      productId: string;
    }
  | {
      type: "setCustomer";
      value: StripeApiCustomerDto;
    }
  | {
      type: "setCoupon";
      value: StripeApiCouponDto | null;
    }
  | {
      type: "setTabIndex";
      value: number;
    }
  | {
      type: "setPriceQuantity";
      priceId: string;
      value: number;
    }
  | {
      type: "setTrialEndTs";
      value: number | undefined;
    }
  | { type: "setAutoScale"; value: boolean }
  | { type: "setCollectionMethod"; value: SubscriptionCollectionMethod }
  | {
      type: "setProrationBehavior";
      value: SubscriptionProrationBehavior;
    }
  | { type: "addPhase" }
  | { type: "removePhase"; value: number }
  | { type: "saveCompleted" };

export function SubscriptionReducer(
  draft: CreateOrModifySubscriptionReducerType,
  action: CreateSubscriptionReducerAction
) {
  let index: number | null = null;
  switch (action.type) {
    case "initModify":
      draft = action.value;
      break;
    case "reset":
      draft = initialCreateSubscriptionReducerState;
      break;
    case "setTabIndex":
      draft.tabIndex = action.value;
      break;
    case "setOrgs":
      draft.orgs = action.value;
      draft.phaseIndex = 0;
      break;
    case "setAgencyId":
      draft.agencyId = action.value;
      break;
    case "setBillingProvider":
      draft.billingProvider = action.value;
      break;
    case "setUsages":
      draft.usages = action.value;
      break;
    case "setPhaseIndex":
      draft.phaseIndex = action.value;
      if (action.value === -1) {
        draft.tabIndex = 0;
      }
      break;
    case "addItem":
      index = draft.phases[draft.phaseIndex].items.findIndex(
        (p) => p.product.id === action.value.id
      );
      if (index === -1) {
        draft.phases[draft.phaseIndex].items.push({
          product: action.value,
          price: null,
          quantity: 0,
          deleted: false,
          isNew: true,
          idx: v4(),
        });
      } else {
        draft.phases[draft.phaseIndex].items[index] = {
          product: action.value,
          price: null,
          quantity: 0,
          deleted: false,
          isNew: true,
          idx: v4(),
        };
      }
      break;
    case "removeItemByProductId":
      draft.phases[draft.phaseIndex].items = draft.phases[
        draft.phaseIndex
      ].items.filter(
        (item) => !(item.product?.id === action.value && item.isNew)
      );

      draft.phases[draft.phaseIndex].items.forEach((item) => {
        if (item.product?.id === action.value) {
          item.deleted = true;
        }
      });

      break;
    case "removeItemByPriceId":
      draft.phases[draft.phaseIndex].items
        .filter((item) => {
          if (item.price?.id === action.value) {
            return !item.isNew;
          }
          return true;
        })
        .map((item) => {
          if (item.price?.id === action.value) {
            item.deleted = true;
          }
        });

      break;
    case "setItemsByTemplate":
      const product = draft.phases[draft.phaseIndex].items.find(
        (i) => i.product.id === action.productId
      )!.product;

      draft.phases[draft.phaseIndex].items = draft.phases[
        draft.phaseIndex
      ].items.filter((i) => !i.isNew && !i.deleted);

      for (const price of action.prices) {
        if (
          price.active &&
          (action.priceIds.some((pid) => price.id.endsWith(pid)) ||
            price.templates.some((t) => t.id === action.template.id))
        ) {
          draft.phases[draft.phaseIndex].items.push({
            idx: v4(),
            price,
            isNew: true,
            product,
            quantity: action.quantity,
            deleted: false,
            isReadOnly: true,
          });
        }
      }

      break;
    case "setCustomer":
      draft.customer = action.value;
      break;
    case "addPriceToItemByIdx":
      index = draft.phases[draft.phaseIndex].items.findIndex(
        (i) => i.price?.id === action.priceId
      );

      if (index === -1) {
        break;
      }
      draft.phases[draft.phaseIndex].items[index].price = action.value;
      break;
    case "addPriceToProduct":
      index = draft.phases[draft.phaseIndex].items.findIndex(
        (i) => i.product.id === action.productId
      );
      if (index === -1) {
        break;
      }
      draft.phases[draft.phaseIndex].items.push({
        ...draft.phases[draft.phaseIndex].items[index],
        price: null,
        isNew: true,
        idx: v4(),
      });
      break;
    case "removeItemByIdx":
      if (draft.phases[draft.phaseIndex].items.length === 1) {
        draft.phases[draft.phaseIndex].items[0].price = null;
        draft.phases[draft.phaseIndex].items[0].isReadOnly = false;
        draft.phases[draft.phaseIndex].items[0].quantity = 0;
      } else {
        draft.phases[draft.phaseIndex].items = draft.phases[
          draft.phaseIndex
        ].items.filter((item) => item.idx !== action.idx);
      }

      break;
    case "setCoupon":
      draft.phases[draft.phaseIndex].coupon = action.value;
      break;
    case "setPriceQuantity":
      for (const item of draft.phases[draft.phaseIndex].items) {
        if (item.price?.id === action.priceId) {
          item.quantity = action.value;
        } else if (item.isReadOnly) {
          item.quantity = action.value;
        }
      }

      // index = draft.phases[draft.phaseIndex].items.findIndex(
      //   (p) => p.price?.id === action.priceId
      // );
      // if (index === -1) {
      //  break;
      // }
      // if (!draft.phases[draft.phaseIndex].items[index].price) {
      //  break;
      // }
      // draft.phases[draft.phaseIndex].items[index].quantity = action.value;
      //
      // if (draft.phases[draft.phaseIndex].items[index].price?.meterId) {
      //   for (const item of draft.phases[draft.phaseIndex].items) {
      //     if (
      //       item.price?.meterId ===
      //         draft.phases[draft.phaseIndex].items[index].price?.meterId &&
      //       item.price?.id !==
      //         draft.phases[draft.phaseIndex].items[index].price?.id
      //     ) {
      //       item.quantity = action.value;
      //     }
      //   }
      // }

      break;
    case "setTrialEndTs":
      draft.phases[draft.phaseIndex].trialEndTs = action.value;
      break;
    case "setCollectionMethod":
      draft.settings.collectionMethod = action.value;
      break;
    case "setProrationBehavior":
      draft.settings.prorationBehavior = action.value;
      break;
    case "setAutoScale":
      draft.settings.autoScale = action.value;
      break;
    case "addPhase":
      const latestPhase = draft.phases[draft.phases.length - 1];
      draft.phases[draft.phases.length - 1].phaseEnd =
        latestPhase.phaseEnd || draft.currentPeriodEnd;

      draft.phases.push({
        ...latestPhase,
        phaseStart: latestPhase.phaseEnd || draft.currentPeriodEnd,
        phaseEnd: null,
      });
      draft.phaseIndex = draft.phases.length - 1;
      draft.pendingChanges = true;
      break;
    case "removePhase":
      const removedPhase = draft.phases[action.value];
      const removedPhaseHadTrial = Boolean(removedPhase.trialEndTs);

      draft.phases.splice(action.value, 1);

      for (let i = action.value; i < draft.phases.length; i++) {
        if (i === 0) {
          continue;
        }

        const previousPhase = draft.phases[i - 1];
        draft.phases[i].phaseStart =
          previousPhase.phaseEnd ||
          previousPhase.trialEndTs ||
          draft.currentPeriodEnd;

        if (i === action.value && removedPhaseHadTrial && previousPhase) {
          draft.phases[i].phaseStart =
            previousPhase.phaseEnd ||
            previousPhase.trialEndTs ||
            draft.currentPeriodEnd;
        }
      }

      if (draft.phaseIndex >= action.value) {
        draft.phaseIndex = Math.max(0, draft.phaseIndex - 1);
      }

      if (draft.phases.length === 0) {
        draft.phases.push({
          phaseStart: 0,
          phaseEnd: 0,
          items: [],
          coupon: null,
          trialEndTs: undefined,
        });
        draft.phaseIndex = 0;
      }
      break;
    case "saveCompleted":
      draft.pendingChanges = false;
      break;
    default:
      break;
  }

  if (
    draft.customer &&
    !draft.customer.email &&
    !draft.customer.name.includes("myshopify.com")
  ) {
    draft.warnings["no-customer-email"] = (
      <IgTypography variant={"body2"}>
        <IgTypography variant={"body2"} sx={{ fontWeight: 600 }}>
          No customer email
        </IgTypography>{" "}
        - This customer will not automatically receive an invoice for this
        subscription. Send link to customer manually.
      </IgTypography>
    );
  }

  if (draft.customer?.email) {
    if (
      draft.billingProvider === "stripe" &&
      draft.customer.email.includes("no-bill")
    ) {
      draft.warnings["bad-email"] = (
        <IgTypography variant={"body2"}>
          <IgTypography variant={"body2"} sx={{ fontWeight: 600 }}>
            Wrong Email Selected
          </IgTypography>{" "}
          - This is a{" "}
          <IgTypography variant={"body2"} sx={{ fontWeight: 600 }}>
            Stripe
          </IgTypography>{" "}
          subscription and the customer's email is{" "}
          <IgTypography variant={"body2"} sx={{ fontWeight: 600 }}>
            {draft.customer.email}
          </IgTypography>
          . Please select or create a Stripe customer using the format{" "}
          {"<Store Name> - <Shop ID>"} for the name and the customer's email.
        </IgTypography>
      );
    } else if (
      draft.billingProvider === "shopify" &&
      !draft.customer.email.includes("no-bill")
    ) {
      draft.warnings["bad-email"] = (
        <IgTypography variant={"body2"}>
          <IgTypography variant={"body2"} sx={{ fontWeight: 600 }}>
            Wrong Email Selected
          </IgTypography>{" "}
          - This is a{" "}
          <IgTypography variant={"body2"} sx={{ fontWeight: 600 }}>
            Shopify
          </IgTypography>{" "}
          subscription and the customer's email is{" "}
          <IgTypography variant={"body2"} sx={{ fontWeight: 600 }}>
            {draft.customer.email}
          </IgTypography>
          . Please select or create a Stripe customer using the format{" "}
          {"<Shop ID>"} for the name and no-bill@intelligems.io for the email.
        </IgTypography>
      );
    } else {
      draft.warnings["bad-email"] = null;
    }
  }

  return draft;
}
